import _ from "lodash";
import IParcel from "../interfaces/IParcel";
import IProperty from "../interfaces/IProperty";
import GeometryService from "../services/geometryService";

const geometryService = new GeometryService();

export function buildProperty(parcels: IParcel[], baseParcelId?: string): IProperty {
    if (!parcels.length)
        throw new Error("Cannot build property without parcels.");

    const sortedParcels = _.sortBy(parcels, p => p.id);
    const parcelIds = sortedParcels.map(p => p.id);
    const baseParcel = sortedParcels.find(p => p.id === baseParcelId) ?? sortedParcels[0];

    const center = geometryService.getShapeCenterPoint(baseParcel.shape);

    const buildingSize = _.sumBy(parcels, p => p.buildingSize || 0);
    const landSize = _.sumBy(parcels, p => p.landSize || 0);
    const salePrice = _.sumBy(parcels, p => p.salePrice || 0);

    return {
        id: parcelIds.join(", "),
        parcelIds,
        baseParcelId: baseParcel.id,
        regridId: baseParcel.id,
        shapes: sortedParcels.map(p => p.shape),
        centerPoint: { latitude: center.lat, longitude: center.lng },
        address: baseParcel.streetAddress,
        city: baseParcel.city,
        county: baseParcel.county,
        municipality: baseParcel.municipality,
        state: baseParcel.state,
        zip: String(baseParcel.zipCode),
        yearBuilt: _.min(parcels.map(p => p.yearBuilt).filter(v => v > 0)) ?? 0,
        landSize: landSize,
        numberOfUnits: _.sumBy(parcels, p => p.numberOfUnits || 0),
        buildingSize: buildingSize,
        ownerEntity: _.uniq(_.compact(parcels.map(p => p.ownerEntity))).join(", ") || null,
        ownerAddress: baseParcel.ownerAddress,
        saleDate: baseParcel.saleDate,
        salePrice: salePrice,
        parcelId: _.uniq(parcels.map(p => p.parcelId)).join(", "),
        currentFar: landSize ? buildingSize / (landSize * 43560) : 0,
        priceSFLand: landSize ? salePrice / (landSize * 43560) : 0,
        priceAcreLand: landSize ? salePrice / landSize : 0,
        landAssessment: _.sumBy(parcels, p => p.landAssessment || 0),
        buildingAssessment: _.sumBy(parcels, p => p.buildingAssessment || 0),
        totalAssessment: _.sumBy(parcels, p => p.totalAssessment || 0),
        numberOfBuildings: _.sumBy(parcels, p => p.numberOfBuildings || 0),
        typeOfStructure: baseParcel.typeOfStructure,
        siteDescription: baseParcel.siteDescription,
        ownershipStructure: baseParcel.ownershipStructure,
        annualTaxBill: _.sumBy(parcels, p => p.annualTaxBill || 0),
        taxYear: baseParcel.taxYear,
        zoning: baseParcel.zoning,
        zoningDescription: baseParcel.zoningDescription,
        zoningSubtype: baseParcel.zoningSubtype,
        zoningObjective: baseParcel.zoningObjective,
        zoningCodeLink: baseParcel.zoningCodeLink,
        permittedLandUses: baseParcel.permittedLandUses,
        permittedLandUsesAsOfRight: baseParcel.permittedLandUsesAsOfRight,
        permittedLandUsesConditional: baseParcel.permittedLandUsesConditional,
        femaFloodZone: baseParcel.femaFloodZone,
        femaFloodZoneSubtype: baseParcel.femaFloodZoneSubtype,
        gradeChangeFeet: _.max(parcels.map(p => p.gradeChangeFeet)) ?? 0,
        distanceToPowerFeet: _.min(parcels.map(p => p.distanceToPowerFeet)) ?? 0,
        femaRiskRating: baseParcel.femaRiskRating,
        medianHouseholdIncome: baseParcel.medianHouseholdIncome,
        householdIncomeGrowthNext5Years: baseParcel.householdIncomeGrowthNext5Years,
        housingAffordabilityIndex: baseParcel.housingAffordabilityIndex,
        populationDensity: baseParcel.populationDensity,
        populationGrowthPast5Years: baseParcel.populationGrowthPast5Years,
        populationGrowthNext5Years: baseParcel.populationGrowthNext5Years,
        housingGrowthPast5Years: baseParcel.housingGrowthPast5Years,
        housingGrowthNext5Years: baseParcel.housingGrowthNext5Years,
    };
}
